import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

/**
 * Distribuição de conversas por canal.
 *
 * Com uma única conta no recorte, mostra a pizza tradicional. Com mais de uma,
 * troca para barras empilhadas por conta — comparar operações distintas numa
 * pizza só esconderia a origem de cada fatia.
 */
const ChannelDistribution = ({ data }) => {
  const accounts = useMemo(
    () => [...new Set((data || []).map((item) => item.account_name).filter(Boolean))],
    [data]
  );

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    if (accounts.length <= 1) {
      return data.map((item) => ({
        name: item.channel_name || item.channel_type,
        value: parseFloat(item.percentage),
        count: parseInt(item.total_conversations, 10),
      }));
    }

    // Uma linha por canal, uma série por conta
    const byChannel = new Map();
    data.forEach((item) => {
      const name = item.channel_name || item.channel_type;
      const row = byChannel.get(name) || { name, total: 0 };
      const count = parseInt(item.total_conversations, 10);
      row[item.account_name] = (row[item.account_name] || 0) + count;
      row.total += count;
      byChannel.set(name, row);
    });

    return [...byChannel.values()].sort((a, b) => b.total - a.total);
  }, [data, accounts]);

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Distribuição por Canal</h3>
        <p className="text-gray-500">Nenhum dado disponível</p>
      </div>
    );
  }

  const multiAccount = accounts.length > 1;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold">Distribuição por Canal</h3>
        {multiAccount && (
          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
            {accounts.length} contas
          </span>
        )}
      </div>

      <ResponsiveContainer width="100%" height={300}>
        {multiAccount ? (
          <BarChart data={chartData} layout="vertical" margin={{ left: 16, right: 16 }}>
            <XAxis type="number" tick={{ fontSize: 12 }} />
            <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(value, name) => [`${value} conversas`, name]} />
            <Legend />
            {accounts.map((account, index) => (
              <Bar
                key={account}
                dataKey={account}
                stackId="conta"
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </BarChart>
        ) : (
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              // Fatias residuais ficam sem rótulo: com muitos canais de volume
              // baixo os textos se sobrepõem e viram um borrão ilegível
              label={({ name, percent }) =>
                percent >= 0.03 ? `${name}: ${(percent * 100).toFixed(0)}%` : ''
              }
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name, props) => [
                `${value}% (${props.payload.count} conversas)`,
                props.payload.name,
              ]}
            />
            <Legend />
          </PieChart>
        )}
      </ResponsiveContainer>

      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {data.map((item, index) => (
          <div
            key={`${item.account_id}-${item.channel_name}-${index}`}
            className="flex items-center justify-between rounded bg-gray-50 p-2"
          >
            <div className="flex min-w-0 items-center">
              <div
                className="mr-2 h-3 w-3 flex-shrink-0 rounded-full"
                style={{
                  backgroundColor: multiAccount
                    ? COLORS[Math.max(accounts.indexOf(item.account_name), 0) % COLORS.length]
                    : COLORS[index % COLORS.length],
                }}
              />
              <div className="min-w-0">
                <span className="block truncate text-sm font-medium">{item.channel_name}</span>
                {multiAccount && (
                  <span className="block truncate text-xs text-gray-500">{item.account_name}</span>
                )}
              </div>
            </div>
            <span className="ml-2 whitespace-nowrap text-sm text-gray-600">
              {item.total_conversations} <span className="text-gray-400">({item.percentage}%)</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChannelDistribution;
